import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './DashboardHandIn.css';
import BrandFeedbackModal from './BrandFeedbackModal';
import OriginalSubmissionModal from './OriginalSubmissionModal';
import RevisionSubmissionModal from './RevisionSubmissionModal';
import ApprovedContentModal from './ApprovedContentModal';
import { useSubmissions } from '../hooks/useSubmissions';
import SubmissionService from '../services/submissionService';
import { addBusinessDays } from '../utils/dateUtils';
import { getCreatorById } from '../services/simpleCreatorService';
import toast from 'react-hot-toast';
import { 
  CheckCircle, 
  Clock, 
  AlertCircle, 
  Eye, 
  MessageSquare, 
  RefreshCw, 
  Calendar, 
  X, 
  FileVideo, 
  Image, 
  FileText, 
  Plus,
  Filter,
  Download,
  ThumbsUp,
  ThumbsDown,
  Send,
  MoreVertical,
  Play,
  Pause,
  Volume2,
  Maximize2,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const DashboardHandIn = ({ openSubmissionId, openCampaignId, openBriefId }) => {
  const [viewMode, setViewMode] = useState('overview'); // 'overview', 'new', 'revisions', 'completed'
  const [selectedCampaign, setSelectedCampaign] = useState('all');
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackData, setFeedbackData] = useState({});
  const [scrollPositions, setScrollPositions] = useState({});
  const [showRevisionConfirmModal, setShowRevisionConfirmModal] = useState(false);
  const [pendingRevisionData, setPendingRevisionData] = useState(null);
  const [pendingRevisionRequest, setPendingRevisionRequest] = useState(null); // Store the revision request data
  const [showApprovedModal, setShowApprovedModal] = useState(false);
  const [approvedSubmission, setApprovedSubmission] = useState(null);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);
  const [creatorProfiles, setCreatorProfiles] = useState({}); // Cache creator profiles by creatorId

  // Fetch real submissions from Firestore
  const { submissions: firestoreSubmissions, loading, error } = useSubmissions();

  // Auto-approve overdue submissions on mount
  useEffect(() => {
    const checkAndAutoApprove = async () => {
      try {
        await SubmissionService.autoApproveOverdueSubmissions();
      } catch (error) {
        console.error('Error in auto-approval check:', error);
      }
    };
    
    checkAndAutoApprove();
  }, []); // Run once on mount

  // Auto-archive completed projects past payment window (14 working days)
  useEffect(() => {
    const checkAndAutoArchive = async () => {
      try {
        const ArchivingService = (await import('../services/archivingService')).default;
        await ArchivingService.autoArchiveCompletedProjects();
      } catch (error) {
        console.error('Error in auto-archive check:', error);
      }
    };
    
    checkAndAutoArchive();
  }, []); // Run once on mount

  // Fetch creator profiles for all submissions
  useEffect(() => {
    const fetchCreatorProfiles = async () => {
      if (!firestoreSubmissions || firestoreSubmissions.length === 0) return;

      const creatorIds = [...new Set(firestoreSubmissions.map(s => s.creatorId).filter(Boolean))];
      const profiles = {};

      await Promise.all(
        creatorIds.map(async (creatorId) => {
          if (!creatorProfiles[creatorId]) {
            try {
              const creatorData = await getCreatorById(creatorId);
              if (creatorData) {
                profiles[creatorId] = creatorData;
              }
            } catch (error) {
              console.error(`Error fetching creator ${creatorId}:`, error);
            }
          } else {
            profiles[creatorId] = creatorProfiles[creatorId];
          }
        })
      );

      setCreatorProfiles(prev => ({ ...prev, ...profiles }));
    };

    fetchCreatorProfiles();
  }, [firestoreSubmissions]);

  const openReviewModal = (submission) => {
    console.log('📋 openReviewModal called:', {
      submissionId: submission.id,
      status: submission.status,
      projectId: submission._originalData?.projectId,
      hasContent: !!submission.content,
      contentLength: submission.content?.length || 0
    });
    setFeedbackData({ submission });
    setShowFeedbackModal(true);
    console.log('✅ Feedback modal state set, showFeedbackModal should be true');
  };

  // Reset hasAutoOpened when openSubmissionId changes
  useEffect(() => {
    if (openSubmissionId) {
      setHasAutoOpened(false);
    }
  }, [openSubmissionId]);

  // Auto-open submission review modal when navigating from CreatorProgressModal
  useEffect(() => {
    if (openSubmissionId && firestoreSubmissions.length > 0 && !showFeedbackModal && !showApprovedModal && !hasAutoOpened) {
      // Small delay to ensure page has rendered
      const timer = setTimeout(() => {
        // Find the submission by ID
        const submission = firestoreSubmissions.find(s => s.id === openSubmissionId);
        if (submission) {
          // Get actual creator profile data from cache (same as when clicking from submissions page)
          const creatorProfile = submission.creatorId ? creatorProfiles[submission.creatorId] : null;
          
          // Map to UI format (matching the format used when clicking from submissions page)
          const mappedSubmission = {
            id: submission.id,
            creator: {
              name: creatorProfile?.name || submission.creatorName || submission.creator?.name || 'Unknown Creator',
              handle: creatorProfile?.handle || submission.creatorHandle || submission.creator?.handle || '@creator',
              avatar: creatorProfile?.profileImage || creatorProfile?.image || submission.creatorAvatar || submission.creator?.avatar || '/images/profile-placeholder.svg',
              followers: creatorProfile?.metrics?.[2]?.replace('Followers: ', '') || submission.creatorFollowers || submission.creator?.followers || 'N/A',
              verified: creatorProfile?.verified || submission.creatorVerified || submission.creator?.verified || false
            },
            campaign: submission.campaignData?.name || submission.campaignName || 'Unknown Campaign',
            brief: submission.briefName || submission.briefData?.title || submission.briefData?.name || 'Unknown Brief',
      status: submission.status,
      submittedAt: submission.submittedAt,
      dueDate: submission.dueDate || (() => {
        const submittedAt = submission.submittedAt instanceof Date 
          ? submission.submittedAt 
          : new Date(submission.submittedAt?.toDate?.() ?? submission.submittedAt);
        return addBusinessDays(submittedAt, 5);
      })(),
            lastRevisionRequestedAt: submission.lastRevisionRequestedAt || null,
            lastResubmittedAt: submission.lastResubmittedAt || null,
            revisionRequestCount: submission.revisionRequestCount || 0,
            revisionCount: submission.revisionCount || 0,
            content: submission.content || [],
            notes: submission.generalNotes || submission.notes || '',
            creatorNotes: submission.creatorNotes || '',
            timestampedNotes: submission.timestampedNotes || [],
            priority: submission.priority || 'medium',
            feedback: submission.feedback || null,
            creatorName: submission.creatorName,
            campaignName: submission.campaignName,
            briefName: submission.briefName,
            _originalData: submission
          };
          
          // Mark as auto-opened to prevent re-opening
          setHasAutoOpened(true);
          
          // Open the appropriate modal based on status
          if (mappedSubmission.status === 'approved') {
            setApprovedSubmission(mappedSubmission);
            setShowApprovedModal(true);
          } else {
            // Use inline function instead of openReviewModal to avoid dependency issues
            setFeedbackData({ submission: mappedSubmission });
            setShowFeedbackModal(true);
          }
        }
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [openSubmissionId, firestoreSubmissions.length, showFeedbackModal, showApprovedModal, hasAutoOpened, creatorProfiles]);

  // Auto-open view mode if navigating from a specific submission
  useEffect(() => {
    if (openSubmissionId && firestoreSubmissions.length > 0) {
      const submission = firestoreSubmissions.find(s => s.id === openSubmissionId);
      if (submission) {
        // Determine which view to show based on submission status
        if (submission.status === 'approved') {
          setViewMode('completed');
        } else if (submission.status === 'revision_submission') {
          setViewMode('revisions');
        } else {
          setViewMode('new');
        }
      }
    }
  }, [openSubmissionId, firestoreSubmissions.length]);
  
  // Map Firestore submissions to match the UI format
  const submissions = firestoreSubmissions.map(submission => {
    // Debug: Log submission status and projectId
    if (submission.status === 'revision_submission') {
      console.log('🔍 Revision Submission Found:', {
        id: submission.id,
        status: submission.status,
        projectId: submission.projectId,
        campaignId: submission.campaignId,
        briefId: submission.briefId,
        creatorId: submission.creatorId,
        revisionCount: submission.revisionCount
      });
    }

    // Get actual creator profile data from cache
    const creatorProfile = submission.creatorId ? creatorProfiles[submission.creatorId] : null;
    
    return {
      id: submission.id,
      creator: {
        name: creatorProfile?.name || submission.creatorName || submission.creator?.name || 'Unknown Creator',
        handle: creatorProfile?.handle || submission.creatorHandle || submission.creator?.handle || '@creator',
        avatar: creatorProfile?.profileImage || creatorProfile?.image || submission.creatorAvatar || submission.creator?.avatar || '/images/profile-placeholder.svg',
        followers: creatorProfile?.metrics?.[2]?.replace('Followers: ', '') || submission.creatorFollowers || submission.creator?.followers || 'N/A',
        verified: creatorProfile?.verified || submission.creatorVerified || submission.creator?.verified || false
      },
      campaign: submission.campaignData?.name || submission.campaignName || 'Unknown Campaign',
      brief: submission.briefName || submission.briefData?.title || submission.briefData?.name || 'Unknown Brief',
      // CRITICAL: Include campaignId and briefId for Firestore operations
      campaignId: submission.campaignId,
      briefId: submission.briefId,
      creatorId: submission.creatorId,
      projectId: submission.projectId,
      status: submission.status,
      submittedAt: submission.submittedAt,
      dueDate: submission.dueDate || (() => {
        const submittedAt = submission.submittedAt instanceof Date 
          ? submission.submittedAt 
          : new Date(submission.submittedAt?.toDate?.() ?? submission.submittedAt);
        return addBusinessDays(submittedAt, 5);
      })(),
      lastRevisionRequestedAt: submission.lastRevisionRequestedAt || null,
      lastResubmittedAt: submission.lastResubmittedAt || null,
      revisionRequestCount: submission.revisionRequestCount || 0,
      revisionCount: submission.revisionCount || 0,
      content: submission.content || [],
      notes: submission.generalNotes || submission.notes || '',
      creatorNotes: submission.creatorNotes || '',
      timestampedNotes: submission.timestampedNotes || [],
      priority: submission.priority || 'medium',
      feedback: submission.feedback || null,
      // Keep original field names for compatibility
      creatorName: submission.creatorName,
      campaignName: submission.campaignName,
      briefName: submission.briefName,
      // Keep original data for actions
      _originalData: submission
    };
  });

  // Show loading state
  if (loading && submissions.length === 0) {
    return (
      <div className="dashboard-handin-section">
        <div className="dashboard-handin-container">
          <div className="dashboard-handin-header">
            <div className="dashboard-handin-header-content">
              <h1 className="dashboard-handin-title">Content Submissions</h1>
              <p className="dashboard-handin-subtitle">Loading submissions...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="dashboard-handin-section">
        <div className="dashboard-handin-container">
          <div className="dashboard-handin-header">
            <div className="dashboard-handin-header-content">
              <h1 className="dashboard-handin-title">Content Submissions</h1>
              <p className="dashboard-handin-subtitle" style={{ color: '#ef4444' }}>
                Error loading submissions: {error}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Keep the mock data as fallback for testing (commented out)
  /*
  const mockSubmissions = [
    {
      id: 1,
      creator: {
        name: "Sarah Johnson",
        handle: "@sarahcreates",
        avatar: "/creatorimages/creatorpic1v2-min.png",
        followers: "125K",
        verified: true
      },
      campaign: "Summer Collection Launch",
      brief: "Main Product Video",
      status: "submitted",
      submittedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000), // 2 days ago
      dueDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000), // 3 days from now
      revisionCount: 0,
      content: [
        {
          id: 'video1',
          title: "Main Product Showcase",
          videoUrl: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerFun.mp4",
          thumbnailUrl: "https://picsum.photos/300/400?random=1",
          duration: 90,
          format: "9:16",
          status: "submitted",
          hook: "This summer essential just changed everything",
          cta: "Shop now - 20% off for first-time buyers",
          type: "product_showcase",
          notes: "This is the main video showcasing our summer collection. I focused on highlighting the key features and showing the product in different lighting conditions. The music choice really matches the summer vibe we're going for.",
          timestampedNotes: [
            { time: 15, timestamp: "0:15", note: "Product introduction - showing the main features" },
            { time: 45, timestamp: "0:45", note: "Lifestyle shot - product in natural summer setting" },
            { time: 75, timestamp: "1:15", note: "Close-up details - highlighting texture and quality" }
          ]
        },
        {
          id: 'video2',
          title: "Day in My Life",
          videoUrl: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4",
          thumbnailUrl: "https://picsum.photos/300/400?random=2",
          duration: 60,
          format: "9:16",
          status: "submitted",
          hook: "POV: You're getting ready with me",
          cta: "Get the look - link in bio",
          type: "day_in_life",
          notes: "This day-in-my-life video shows how I naturally incorporate the product into my daily routine. I wanted to make it feel authentic and relatable, showing the product in real-life situations.",
          timestampedNotes: [
            { time: 10, timestamp: "0:10", note: "Morning routine - showing product in natural light" },
            { time: 35, timestamp: "0:35", note: "Getting ready - product as part of daily routine" },
            { time: 50, timestamp: "0:50", note: "Final look - showcasing the end result" }
          ]
        },
        {
          id: 'video3',
          title: "Hook 1",
          videoUrl: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerEscapes.mp4",
          thumbnailUrl: "https://picsum.photos/300/400?random=3",
          duration: 85,
          format: "9:16",
          status: "submitted",
          hook: "Wait until you see the results",
          cta: "Shop now - 20% off for first-time buyers",
          type: "product_showcase",
          variationType: "hook",
          notes: "This is the first hook variation. I tried a more mysterious approach with 'Wait until you see the results' to create curiosity and anticipation.",
          timestampedNotes: [
            { time: 5, timestamp: "0:05", note: "Hook delivery - building anticipation" },
            { time: 30, timestamp: "0:30", note: "Product reveal - showing the transformation" },
            { time: 60, timestamp: "1:00", note: "Results showcase - before and after" }
          ]
        },
        {
          id: 'video4',
          title: "Hook 2",
          videoUrl: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerJoyrides.mp4",
          thumbnailUrl: "https://picsum.photos/300/400?random=4",
          duration: 88,
          format: "9:16",
          status: "submitted",
          hook: "This is going viral",
          cta: "Shop now - 20% off for first-time buyers",
          type: "product_showcase",
          variationType: "hook"
        },
        {
          id: 'video5',
          title: "CTA 1",
          videoUrl: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerFun.mp4",
          thumbnailUrl: "https://picsum.photos/300/400?random=5",
          duration: 92,
          format: "9:16",
          status: "submitted",
          hook: "This summer essential just changed everything",
          cta: "Limited time offer - ends soon",
          type: "product_showcase",
          variationType: "cta"
        },
        {
          id: 'video6',
          title: "CTA 2",
          videoUrl: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4",
          thumbnailUrl: "https://picsum.photos/300/400?random=6",
          duration: 87,
          format: "9:16",
          status: "submitted",
          hook: "This summer essential just changed everything",
          cta: "Use code SUMMER20 for 20% off",
          type: "product_showcase",
          variationType: "cta"
        }
      ],
      notes: "Love the energy in this video! The product shots are perfect. I made sure to highlight the key features and show the product in different lighting conditions. The music choice really matches the summer vibe we're going for.",
      creatorNotes: "I focused on showcasing the product's versatility and summer appeal. Made sure to include lifestyle shots and close-up product details.",
      timestampedNotes: [
        {
          time: 15,
          timestamp: "0:15",
          note: "Product introduction - showing the main features"
        },
        {
          time: 45,
          timestamp: "0:45", 
          note: "Lifestyle shot - product in natural summer setting"
        },
        {
          time: 75,
          timestamp: "1:15",
          note: "Close-up details - highlighting texture and quality"
        }
      ],
      priority: "high"
    },
    {
      id: 2,
      creator: {
        name: "Mike Chen",
        handle: "@mikevideos",
        avatar: "/creatorimages/Creatorpic2v2-min.png",
        followers: "89K",
        verified: false
      },
      campaign: "Tech Product Demo",
      brief: "App Demonstration",
      status: "revision_submission",
      submittedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000), // 1 day ago
      dueDate: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000), // 2 days from now
      revisionCount: 1,
      content: [
        {
          id: 'video1',
          title: "App Tutorial",
          videoUrl: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ElephantsDream.mp4",
          thumbnailUrl: "https://picsum.photos/300/400?random=5",
          duration: 120,
          format: "16:9",
          status: "revision_submission",
          description: "Step-by-step tutorial showing how to use the app",
          hook: "This app just made my life 10x easier",
          cta: "Download now - free trial available",
          type: "tutorial"
        },
        {
          id: 'video2',
          title: "Feature Highlights",
          videoUrl: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerFun.mp4",
          thumbnailUrl: "https://picsum.photos/300/400?random=6",
          duration: 90,
          format: "16:9",
          status: "revision_submission",
          description: "Quick overview of the app's best features",
          hook: "Wait until you see what this can do",
          cta: "Try it free for 7 days",
          type: "feature_highlight"
        },
        {
          id: 'video3',
          title: "User Testimonial",
          videoUrl: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4",
          thumbnailUrl: "https://picsum.photos/300/400?random=7",
          duration: 60,
          format: "16:9",
          status: "revision_submission",
          description: "Real user sharing their experience with the app",
          hook: "I've been using this for 3 months now",
          cta: "Join thousands of happy users",
          type: "testimonial"
        }
      ],
      notes: "Great concept but need clearer feature explanations in the middle section.",
      priority: "medium",
      feedback: {
        hasFeedback: true,
        generalNotes: "The app demo is well-structured but we need clearer explanations of the key features. The pacing is good but some features need more detail.",
        timestampedFeedback: [
          { time: 30, note: "Feature explanation at 30 seconds needs to be clearer", type: "revision" },
          { time: 60, note: "Perfect transition at 1:00!", type: "positive" },
          { time: 90, note: "The call-to-action at 1:30 could be more prominent", type: "revision" }
        ],
        deadline: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000)
      }
    },
    {
      id: 3,
      creator: {
        name: "Emma Rodriguez",
        handle: "@emmacontent",
        avatar: "/creatorimages/creatorpic3v2-min.png",
        followers: "203K",
        verified: true
      },
      campaign: "Beauty Tutorial Series",
      brief: "Makeup Tutorial",
      status: "approved",
      submittedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000), // 3 days ago
      dueDate: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000), // 1 day from now
      content: [
        {
          id: 'video1',
          title: "Full Face Tutorial",
          videoUrl: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4",
          thumbnailUrl: "https://picsum.photos/300/400?random=8",
          duration: 180,
          format: "9:16",
          status: "approved",
          description: "Complete full face makeup tutorial using our new product line",
          hook: "This is the easiest full face routine ever",
          cta: "Shop the products - link in bio",
          type: "tutorial"
        },
        {
          id: 'video2',
          title: "Quick 5-Minute Look",
          videoUrl: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerEscapes.mp4",
          thumbnailUrl: "https://picsum.photos/300/400?random=9",
          duration: 90,
          format: "9:16",
          status: "approved",
          description: "Quick 5-minute makeup routine for busy mornings",
          hook: "5 minutes to look like this",
          cta: "Get the look - code EMMA20",
          type: "quick_tutorial"
        },
        {
          id: 'video3',
          title: "Product Review",
          videoUrl: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerJoyrides.mp4",
          thumbnailUrl: "https://picsum.photos/300/400?random=10",
          duration: 120,
          format: "9:16",
          status: "approved",
          description: "Honest review of the new product line with pros and cons",
          hook: "I've been testing this for 2 weeks",
          cta: "Try it yourself - free shipping",
          type: "review"
        }
      ],
      notes: "Perfect execution! Ready to publish.",
      priority: "low"
    },
    {
      id: 4,
      creator: {
        name: "Alex Thompson",
        handle: "@alexcreates",
        avatar: "/creatorimages/creatorpic1v2-min.png",
        followers: "156K",
        verified: true
      },
      campaign: "Holiday Campaign 2024",
      brief: "Behind the Scenes",
      status: "submitted",
      submittedAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000), // 4 days ago
      dueDate: new Date(Date.now() + 1 * 24 * 60 * 60 * 1000), // 1 day from now
      content: [
        {
          id: 'video4',
          title: "Behind the Scenes",
          videoUrl: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerEscapes.mp4",
          thumbnailUrl: "https://picsum.photos/300/400?random=4",
          duration: 60,
          format: "9:16",
          status: "submitted",
          description: "Behind the scenes content from the photoshoot"
        }
      ],
      notes: "Great behind the scenes content! Really captured the authentic moments and showed the creative process. The candid shots add a lot of personality to the campaign.",
      creatorNotes: "I wanted to show the authentic side of the photoshoot - the real moments, the creative process, and the team working together. This gives viewers a behind-the-scenes look at how we create content.",
      timestampedNotes: [
        {
          time: 8,
          timestamp: "0:08",
          note: "Setting up the shot - showing the preparation process"
        },
        {
          time: 25,
          timestamp: "0:25",
          note: "Photographer directing - capturing the creative direction"
        },
        {
          time: 40,
          timestamp: "0:40",
          note: "Natural moment - candid shot of the team laughing"
        },
        {
          time: 55,
          timestamp: "0:55",
          note: "Final shot setup - showing the end result"
        }
      ],
      priority: "high"
    },
    {
      id: 3,
      creator: {
        name: "Emma Davis",
        handle: "@emmacreates",
        avatar: "/creatorimages/creatorpic1v2-min.png",
        followers: "89K",
        verified: false
      },
      campaign: "Product Launch Q1",
      brief: "Tutorial Video",
      status: "revision_submission",
      submittedAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000), // 1 day ago
      dueDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000), // 5 days from now
      revisionCount: 2,
      content: [
        {
          id: 'video3',
          title: "How to Use Product",
          videoUrl: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerFun.mp4",
          thumbnailUrl: "https://picsum.photos/300/400?random=3",
          duration: 120,
          format: "16:9",
          status: "revision_submission",
          description: "Step-by-step tutorial showing how to use the new product features"
        }
      ],
      notes: "Need to add more detail in the middle section",
      creatorNotes: "I created a comprehensive tutorial covering the basic features first, then moving to intermediate functions. I made sure to include clear explanations and visual demonstrations for each step. The pacing allows viewers to follow along easily.",
      timestampedNotes: [
        {
          time: 12,
          timestamp: "0:12",
          note: "Introduction - explaining what we'll cover in this tutorial"
        },
        {
          time: 30,
          timestamp: "0:30",
          note: "Basic setup - showing how to get started with the product"
        },
        {
          time: 65,
          timestamp: "1:05",
          note: "Core features demonstration - the main functionality"
        },
        {
          time: 90,
          timestamp: "1:30",
          note: "Advanced tips - pro tips for power users"
        },
        {
          time: 110,
          timestamp: "1:50",
          note: "Conclusion - wrapping up and next steps"
        }
      ],
      priority: "medium",
      feedback: {
        hasFeedback: true,
        generalNotes: "Please add more detail in the middle section about the advanced features",
        timestampedFeedback: [
          {
            time: 45,
            timestamp: "0:45",
            type: "revision",
            note: "Add more detail about the advanced features here"
          }
        ]
      }
    }
  ];
  */

  // Helper functions
  const getTimeRemaining = (dueDate) => {
    const now = new Date();
    const diff = dueDate.getTime() - now.getTime();
    
    if (diff <= 0) return 'Overdue';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days}d ${hours}h`;
    return `${hours}h`;
  };


  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#ef4444';
      case 'medium': return '#f59e0b';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getFeedbackTypeIcon = (type) => {
    switch (type) {
      case 'revision': return '🔧';
      case 'positive': return '✅';
      default: return '💬';
    }
  };

  const getFeedbackTypeColor = (type) => {
    switch (type) {
      case 'revision': return '#ef4444';
      case 'positive': return '#10b981';
      default: return '#6b7280';
    }
  };

  // Calculate counts for each category (used in overview)
  const newSubmissionsCount = submissions.filter(s => s.status === 'submitted').length;
  const revisionsCount = submissions.filter(s => s.status === 'needs_revision' || s.status === 'revision_submission').length;
  const completedCount = submissions.filter(s => s.status === 'approved').length;

  // First, filter submissions based on view mode
  const viewModeFilteredSubmissions = submissions.filter(submission => {
    if (viewMode === 'overview') {
      return true;
    } else {
      switch (viewMode) {
        case 'new':
          return submission.status === 'submitted';
        case 'revisions':
          return submission.status === 'needs_revision' || submission.status === 'revision_submission';
        case 'completed':
          return submission.status === 'approved';
        default:
          return true;
      }
    }
  });

  // Get unique campaigns from filtered submissions for the filter dropdown
  const campaigns = [...new Set(viewModeFilteredSubmissions.map(s => s.campaign))].sort();

  // Then, filter by campaign
  const filteredSubmissions = viewModeFilteredSubmissions.filter(submission => {
    // Filter by campaign
    const matchesCampaign = selectedCampaign === 'all' || submission.campaign === selectedCampaign;
    return matchesCampaign;
  });

  const handleSubmissionClick = (submission) => {
    console.log('🔍 handleSubmissionClick called:', {
      submissionId: submission.id,
      status: submission.status,
      projectId: submission._originalData?.projectId,
      hasOriginalData: !!submission._originalData
    });
    
    // If submission is approved, show the ApprovedContentModal
    if (submission.status === 'approved') {
      setApprovedSubmission(submission);
      setShowApprovedModal(true);
    } else {
      // Otherwise show the review/feedback modal (this will open OriginalSubmissionModal or RevisionSubmissionModal based on status)
      console.log('Opening review modal for submission with status:', submission.status);
      openReviewModal(submission);
    }
  };

  const scrollSubmissions = (campaignName, direction) => {
    const container = document.getElementById(`submissions-${campaignName.replace(/\s+/g, '-')}`);
    if (container) {
      const scrollAmount = 350; // Width of one card + gap
      const newPosition = direction === 'left' 
        ? container.scrollLeft - scrollAmount 
        : container.scrollLeft + scrollAmount;
      
      container.scrollTo({
        left: newPosition,
        behavior: 'smooth'
      });
      
      setScrollPositions(prev => ({
      ...prev,
        [campaignName]: newPosition
      }));
    }
  };

  const handleApprove = async (submissionId) => {
    console.log('Approving submission:', submissionId);
    
    try {
      // Find the submission data
      const submission = submissions.find(s => s.id === submissionId);
      if (!submission) {
        console.error('Submission not found:', submissionId);
        return;
      }

      // Update submission status to approved in Firestore
      // This returns projectId and creatorId for use in subsequent operations
      const originalData = submission._originalData || submission;
      const approvalResult = await SubmissionService.approveAllVideos(
        originalData.campaignId,
        originalData.briefId,
        submissionId
      );

      // Use the projectId returned from approveAllVideos (ensures consistency)
      const projectId = approvalResult.projectId;
      const creatorId = approvalResult.creatorId || originalData.creatorId;

      if (!projectId) {
        throw new Error('Failed to get projectId from approval');
      }

      // Move project to completed status using the same projectId and submissionId
      await SubmissionService.completeProject(
        originalData.campaignId,
        originalData.briefId,
        creatorId,
        projectId,
        submissionId // Pass submissionId for archiving access
      );

      // Close chat and send completion message using the same projectId
      await SubmissionService.closeChatWithCompletionMessage(
        originalData.campaignId,
        originalData.briefId,
        creatorId,
        projectId
      );

      // Show approved modal
      setApprovedSubmission(submission);
      setShowApprovedModal(true);
      
      // Close the current feedback modal
      setShowFeedbackModal(false);
      setFeedbackData({});

    } catch (error) {
      console.error('Error approving submission:', error);
      // Handle error - maybe show a toast notification
    }
  };

  const handleRequestRevision = (submission, revisionRequestData = null) => {
    console.log('handleRequestRevision called with:', submission, revisionRequestData);
    
    // If revisionRequestData is provided, it means we're showing confirmation after adding notes
    if (revisionRequestData) {
      // Store the submission data (use the mapped submission format for display)
      setPendingRevisionData(submission);
      setPendingRevisionRequest(revisionRequestData);
      setShowRevisionConfirmModal(true);
      // Close the revision request modal (it's inside the submission modal)
      // The submission modal will stay open, but we'll show confirmation on top
      return;
    }
    
    // If this is a revision submission, it means we're already viewing it in RevisionSubmissionModal
    // The RevisionSubmissionModal handles its own revision request flow internally
    if (submission && submission.status === 'revision_submission') {
      // Already in revision submission modal - don't interfere with its internal flow
      console.log('Revision request handled within RevisionSubmissionModal - not interfering');
      return;
    }
    
    // This shouldn't be called directly anymore - revision request should go through the modal first
    console.warn('handleRequestRevision called without revisionRequestData - this should not happen');
  };

  const proceedWithRevision = () => {
    // After confirming, close confirmation modal and reopen the feedback modal
    // so user can actually submit the revision
    setShowRevisionConfirmModal(false);
    if (pendingRevisionData) {
      // Reopen the feedback modal which will show RevisionRequestModal
      openReviewModal({ submission: pendingRevisionData, _originalData: pendingRevisionData });
    }
    setPendingRevisionData(null);
  };

  const handleLeaveReview = async (submission, reviewData) => {
    try {
      // Submit review to Firestore with full submission object
      await SubmissionService.submitReview(submission, reviewData);
      console.log('Review submitted successfully');
    } catch (error) {
      console.error('Error submitting review:', error);
      throw error;
    }
  };

  const confirmRevisionRequest = async () => {
    if (pendingRevisionData && pendingRevisionRequest) {
      try {
        console.log('Submitting revision request for:', pendingRevisionData.id);
        
        // Get the original Firestore data
        const originalData = pendingRevisionData._originalData || pendingRevisionData;
        
        // Format videos for revision
        const videosForRevision = pendingRevisionRequest.selectedVideos.map(video => ({
          videoId: video.id,
          feedback: {
            // Use video-specific general notes if available, otherwise fall back to combined generalFeedback
            generalNotes: pendingRevisionRequest.videoGeneralNotes?.[video.id] || 
                         (pendingRevisionRequest.generalFeedback || ''),
            timestampedFeedback: (pendingRevisionRequest.timestampedFeedback || [])
              .filter(fb => fb.videoId === video.id)
          }
        }));

        // Actually submit the revision request
        const submissionId = pendingRevisionData.id;
        await SubmissionService.requestRevision(
          originalData.campaignId,
          originalData.briefId,
          submissionId,
          videosForRevision,
          pendingRevisionRequest.generalFeedback
        );

        toast.success('Revision request sent to creator!');
        
        // Close confirmation modal and submission modal
        setShowRevisionConfirmModal(false);
        setShowFeedbackModal(false);
        setFeedbackData({});
        setPendingRevisionData(null);
        setPendingRevisionRequest(null);
      } catch (error) {
        console.error('Error submitting revision request:', error);
        toast.error('Failed to submit revision request: ' + error.message);
      }
    }
  };

  const cancelRevisionRequest = () => {
    setShowRevisionConfirmModal(false);
    setPendingRevisionData(null);
  };

  const handleReject = (submissionId) => {
    console.log('Rejecting submission:', submissionId);
    // In real app, this would update the database
  };

  // Show overview cards when viewMode is 'overview'
  if (viewMode === 'overview') {
    return (
      <div className="dashboard-handin-section">
        <div className="dashboard-handin-container">
          {/* Header */}
          <div className="dashboard-handin-header">
            <div className="dashboard-handin-header-content">
              <h1 className="dashboard-handin-title">Content Submissions</h1>
              <p className="dashboard-handin-subtitle">Review and approve creator content submissions</p>
            </div>
          </div>

          {/* Overview Cards */}
          <div className="dashboard-handin-overview-cards">
            {/* New Submissions Card */}
            <div 
              className={`dashboard-handin-overview-card dashboard-handin-new-submissions ${newSubmissionsCount > 0 ? 'dashboard-handin-has-new' : ''}`}
              onClick={() => setViewMode('new')}
            >
              <div className="dashboard-handin-overview-card-header">
                <div className="dashboard-handin-overview-card-icon">
                  <FileText size={32} />
                </div>
                <div className="dashboard-handin-overview-card-title">
                  <h3>New Submissions</h3>
                  <p>First-time content submissions ready for review</p>
                </div>
              </div>
              <div className="dashboard-handin-overview-card-count">
                <span className="dashboard-handin-overview-count-number">{newSubmissionsCount}</span>
                <span className="dashboard-handin-overview-count-label">Items</span>
              </div>
              <div className="dashboard-handin-overview-card-action">
                <span className="dashboard-handin-overview-action-text">View All →</span>
              </div>
            </div>

            {/* Revisions Card */}
            <div 
              className="dashboard-handin-overview-card dashboard-handin-revisions"
              onClick={() => setViewMode('revisions')}
            >
              <div className="dashboard-handin-overview-card-header">
                <div className="dashboard-handin-overview-card-icon">
                  <RefreshCw size={32} />
                </div>
                <div className="dashboard-handin-overview-card-title">
                  <h3>Revisions</h3>
                  <p>Resubmitted content after revision requests</p>
                </div>
              </div>
              <div className="dashboard-handin-overview-card-count">
                <span className="dashboard-handin-overview-count-number">{revisionsCount}</span>
                <span className="dashboard-handin-overview-count-label">Items</span>
              </div>
              <div className="dashboard-handin-overview-card-action">
                <span className="dashboard-handin-overview-action-text">View All →</span>
              </div>
            </div>

            {/* Completed Card */}
            <div 
              className="dashboard-handin-overview-card dashboard-handin-completed"
              onClick={() => setViewMode('completed')}
            >
              <div className="dashboard-handin-overview-card-header">
                <div className="dashboard-handin-overview-card-icon">
                  <CheckCircle size={32} />
                </div>
                <div className="dashboard-handin-overview-card-title">
                  <h3>Completed</h3>
                  <p>Approved content ready for use</p>
                </div>
              </div>
              <div className="dashboard-handin-overview-card-count">
                <span className="dashboard-handin-overview-count-number">{completedCount}</span>
                <span className="dashboard-handin-overview-count-label">Items</span>
              </div>
              <div className="dashboard-handin-overview-card-action">
                <span className="dashboard-handin-overview-action-text">View All →</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-handin-section">
      
      <div className="dashboard-handin-container">
        {/* Header with Breadcrumb */}
        <div className="dashboard-handin-header">
          <div className="dashboard-handin-header-content">
            {viewMode !== 'overview' && (
              <div className="dashboard-handin-breadcrumb">
                <button 
                  className="dashboard-handin-breadcrumb-back"
                  onClick={() => setViewMode('overview')}
                >
                  <ChevronLeft size={20} />
                  <span>Back to Overview</span>
                </button>
              </div>
            )}
            <h1 className="dashboard-handin-title">
              {viewMode === 'new' ? 'New Submissions' : 
               viewMode === 'revisions' ? 'Revisions' : 
               viewMode === 'completed' ? 'Completed' : 
               'Content Submissions'}
            </h1>
            <p className="dashboard-handin-subtitle">
              {viewMode === 'new' ? 'First-time content submissions ready for review' :
               viewMode === 'revisions' ? 'Resubmitted content after revision requests' :
               viewMode === 'completed' ? 'Approved content ready for use' :
               'Review and approve creator content submissions'}
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="dashboard-handin-controls">
          <div className="dashboard-handin-filters">
            <div className="dashboard-handin-campaign-filter">
              <Filter size={16} />
              <select
                value={selectedCampaign}
                onChange={(e) => setSelectedCampaign(e.target.value)}
                className="dashboard-handin-campaign-select"
              >
                <option value="all">All Campaigns</option>
                {campaigns.map(campaign => (
                  <option key={campaign} value={campaign}>{campaign}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Submission Cards - Grouped by Campaign */}
        <div className="dashboard-handin-submissions-container">
          {(() => {
            // Group submissions by campaign
            const groupedSubmissions = filteredSubmissions.reduce((groups, submission) => {
              const campaign = submission.campaign;
              if (!groups[campaign]) {
                groups[campaign] = [];
              }
              groups[campaign].push(submission);
              return groups;
            }, {});

            console.log('Grouped submissions:', groupedSubmissions);

            return Object.entries(groupedSubmissions).map(([campaignName, submissions]) => (
              <div key={campaignName} className="dashboard-handin-campaign-group">
                <div className="dashboard-handin-campaign-header">
                  <h3 className="dashboard-handin-campaign-title">{campaignName}</h3>
                  <span className="dashboard-handin-campaign-count">{submissions.length} submission{submissions.length !== 1 ? 's' : ''}</span>
                </div>
                
                <div className="dashboard-handin-submissions-wrapper">
                  <div 
                    id={`submissions-${campaignName.replace(/\s+/g, '-')}`}
                    className="dashboard-handin-submissions"
                  >
                    {submissions.map(submission => {
                      const dueDate = submission.dueDate instanceof Date 
                        ? submission.dueDate 
                        : (submission.dueDate?.toDate ? submission.dueDate.toDate() : new Date(submission.dueDate));
                      const isUrgent = submission.status !== 'approved' && new Date() > dueDate;
                      
                      return (
                        <div 
                          key={submission.id} 
                          className={`dashboard-handin-submission-card ${isUrgent ? 'dashboard-handin-card-urgent' : ''}`}
                          onClick={() => handleSubmissionClick(submission)}
                        >
                    <div className="dashboard-handin-card-header">
                      <div className="dashboard-handin-creator-info">
                        <div className="dashboard-handin-creator-avatar">
                          <img src={submission.creator.avatar} alt={submission.creator.name} />
                        </div>
                        <div className="dashboard-handin-creator-details">
                          <h4 className="dashboard-handin-creator-name">{submission.creator.name}</h4>
                          <p className="dashboard-handin-creator-handle">{submission.creator.handle}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="dashboard-handin-card-content">
                      <div className="dashboard-handin-campaign-info">
                        <h5 className="dashboard-handin-campaign-name">{submission.campaign}</h5>
                        <p className="dashboard-handin-brief-name">{submission.brief}</p>
                      </div>
                      
                      {/* Status Tags */}
                      <div className="dashboard-handin-status-tags">
                        {/* New Submission Tag - Shows for 5 days (review period before auto-approval) */}
                        {submission.status === 'submitted' && (() => {
                          const submittedAt = submission.submittedAt instanceof Date 
                            ? submission.submittedAt 
                            : new Date(submission.submittedAt);
                          const daysSinceSubmission = Math.floor((Date.now() - submittedAt.getTime()) / (1000 * 60 * 60 * 24));
                          return daysSinceSubmission <= 5 ? (
                            <div className="dashboard-handin-new-tag">
                              <span>New</span>
                            </div>
                          ) : null;
                        })()}
                        
                        {/* Revision Status Badge */}
                        {(submission.status === 'revision_requested' || submission.status === 'needs_revision') && (
                          <div className="dashboard-handin-revision-badge">
                            <RefreshCw size={14} />
                            <span>Revision Requested</span>
                          </div>
                        )}
                        
                        {/* Revision Submitted Badge */}
                        {submission.status === 'revision_submission' && (
                          <>
                            <div className="dashboard-handin-revision-submitted-badge">
                              <CheckCircle size={14} />
                              <span>
                                {submission.revisionCount === 0 
                                  ? 'Original Submission' 
                                  : `Revision ${submission.revisionCount} Submitted`}
                              </span>
                            </div>
                            {/* New Revision Tag - Shows for 5 days after resubmission */}
                            {(() => {
                              const resubmittedAt = submission.lastResubmittedAt || submission.submittedAt;
                              if (!resubmittedAt) return null;
                              
                              const resubmittedDate = resubmittedAt instanceof Date 
                                ? resubmittedAt 
                                : resubmittedAt?.toDate 
                                ? resubmittedAt.toDate() 
                                : new Date(resubmittedAt);
                              const daysSinceResubmission = Math.floor((Date.now() - resubmittedDate.getTime()) / (1000 * 60 * 60 * 24));
                              
                              return daysSinceResubmission <= 5 ? (
                                <div className="dashboard-handin-new-tag">
                                  <span>New</span>
                                </div>
                              ) : null;
                            })()}
                          </>
                        )}
                      </div>
                      
                      <div className="dashboard-handin-metrics-grid">
                        <div className="dashboard-handin-metric-item">
                          <div className="dashboard-handin-metric-icon">
                            <FileVideo size={16} />
                          </div>
                          <div className="dashboard-handin-metric-content">
                            <span className="dashboard-handin-metric-value">{submission.content.length}</span>
                            <span className="dashboard-handin-metric-label">Videos</span>
                          </div>
                        </div>
                        
                        <div className="dashboard-handin-metric-item">
                          <div className="dashboard-handin-metric-icon">
                            <RefreshCw size={16} />
                          </div>
                          <div className="dashboard-handin-metric-content">
                            <span className="dashboard-handin-metric-value">
                              {submission.content.filter(video => video.variationType).length}
                            </span>
                            <span className="dashboard-handin-metric-label">Variations</span>
                          </div>
                        </div>
                        
                        {submission.status === 'approved' ? (
                          <div className="dashboard-handin-metric-item dashboard-handin-approved-metric-item">
                            <div className="dashboard-handin-metric-icon dashboard-handin-approved-icon">
                              <CheckCircle size={16} />
                            </div>
                            <div className="dashboard-handin-metric-content dashboard-handin-approved-metric-content">
                              <span className="dashboard-handin-metric-value dashboard-handin-approved-text">Approved</span>
                            </div>
                          </div>
                        ) : (
                          <div className="dashboard-handin-metric-item">
                            <div className="dashboard-handin-metric-icon">
                              <Calendar size={16} />
                            </div>
                            <div className="dashboard-handin-metric-content">
                              <span className="dashboard-handin-metric-value">
                                {(() => {
                                  const dueDate = submission.dueDate instanceof Date 
                                    ? submission.dueDate 
                                    : new Date(submission.dueDate);
                                  const timeLeft = dueDate.getTime() - Date.now();
                                  const hoursLeft = Math.ceil(timeLeft / (1000 * 60 * 60));
                                  const daysLeft = Math.ceil(timeLeft / (1000 * 60 * 60 * 24));
                                  
                                  // Show hours if less than 24 hours remaining, otherwise show days (using ceil to show full days)
                                  if (timeLeft <= 0) {
                                    return '0d';
                                  } else if (hoursLeft < 24) {
                                    return `${hoursLeft}h`;
                                  } else {
                                    return `${daysLeft}d`;
                                  }
                                })()}
                              </span>
                              <span className="dashboard-handin-metric-label">Left</span>
                            </div>
                          </div>
                        )}
                        
                        {submission.status === 'approved' ? (
                          <div className="dashboard-handin-metric-item">
                            <div className="dashboard-handin-metric-icon">
                              <Calendar size={16} />
                            </div>
                            <div className="dashboard-handin-metric-content">
                              <span className="dashboard-handin-metric-value">1 year</span>
                              <span className="dashboard-handin-metric-label">Usage</span>
                            </div>
                          </div>
                        ) : (
                          <div className="dashboard-handin-metric-item">
                            <div className="dashboard-handin-metric-icon">
                              <Clock size={16} />
                            </div>
                            <div className="dashboard-handin-metric-content">
                              <span className="dashboard-handin-metric-value">
                                {submission.content.reduce((total, video) => total + video.duration, 0)}s
                              </span>
                              <span className="dashboard-handin-metric-label">Total</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="dashboard-handin-card-footer">
                      <div className="dashboard-handin-submission-meta">
                        <span className="dashboard-handin-submission-date">
                          {submission.submittedAt.toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                );
                    })}
                  </div>
                  
                  {submissions.length > 3 && (
                    <div className="dashboard-handin-scroll-controls">
                      <button 
                        className="dashboard-handin-scroll-btn dashboard-handin-scroll-left"
                        onClick={() => scrollSubmissions(campaignName, 'left')}
                      >
                        <ChevronLeft size={20} />
                      </button>
                <button
                        className="dashboard-handin-scroll-btn dashboard-handin-scroll-right"
                        onClick={() => scrollSubmissions(campaignName, 'right')}
                >
                        <ChevronRight size={20} />
                </button>
                    </div>
                  )}
                </div>
              </div>
            ));
          })()}
          
          {filteredSubmissions.length === 0 && (
            <div className="dashboard-handin-no-submissions">
              <div className="dashboard-handin-no-submissions-icon">📤</div>
              <h3>No Submissions Found</h3>
              <p>No content matches the current filter criteria.</p>
            </div>
            )}
          </div>

      </div>

      {/* Submission Modals - Choose based on status */}
      {showFeedbackModal && feedbackData.submission && (
        <>
          {/* Debug: Log submission status */}
          {console.log('🔍 Modal Selection Debug:', {
            hasSubmission: !!feedbackData.submission,
            status: feedbackData.submission?.status,
            submissionId: feedbackData.submission?.id,
            isSubmitted: feedbackData.submission?.status === 'submitted',
            isNeedsRevision: feedbackData.submission?.status === 'needs_revision',
            isRevisionSubmission: feedbackData.submission?.status === 'revision_submission',
            projectId: feedbackData.submission?._originalData?.projectId
          })}
          
          {/* Original Submission Modal */}
          {(feedbackData.submission.status === 'submitted' || 
            feedbackData.submission.status === 'needs_revision') && (
            <OriginalSubmissionModal
              isOpen={showFeedbackModal}
              onClose={() => {
                setShowFeedbackModal(false);
                setFeedbackData({});
                // Mark as auto-opened to prevent reopening
                if (openSubmissionId) {
                  setHasAutoOpened(true);
                }
              }}
              submission={feedbackData.submission}
              onApprove={handleApprove}
              onReject={handleReject}
              onRequestRevision={handleRequestRevision}
            />
          )}
          
          {/* Revision Submission Modal */}
          {feedbackData.submission && feedbackData.submission.status === 'revision_submission' && (
            <>
              {console.log('🎯 Opening RevisionSubmissionModal:', {
                isOpen: showFeedbackModal,
                status: feedbackData.submission.status,
                submissionId: feedbackData.submission.id,
                projectId: feedbackData.submission._originalData?.projectId
              })}
              <RevisionSubmissionModal
                isOpen={showFeedbackModal}
                onClose={() => {
                  setShowFeedbackModal(false);
                  setFeedbackData({});
                  // Mark as auto-opened to prevent reopening
                  if (openSubmissionId) {
                    setHasAutoOpened(true);
                  }
                }}
                submission={feedbackData.submission}
                onApprove={handleApprove}
                onReject={handleReject}
                onRequestRevision={handleRequestRevision}
              />
            </>
          )}
          
          {/* Fallback to Brand Feedback Modal for other statuses */}
          {!['submitted', 'needs_revision', 'revision_submission'].includes(feedbackData.submission.status) && (
            <BrandFeedbackModal
              isOpen={showFeedbackModal}
              onClose={() => {
                setShowFeedbackModal(false);
                setFeedbackData({});
                // Reset auto-open flag when modal is closed
                if (openSubmissionId) {
                  setHasAutoOpened(true);
                }
              }}
              submission={feedbackData.submission}
              onApprove={handleApprove}
              onReject={handleReject}
              onRequestRevision={handleRequestRevision}
            />
          )}
        </>
      )}

      {/* Approved Content Modal */}
      {showApprovedModal && approvedSubmission && (
        <ApprovedContentModal
          isOpen={showApprovedModal}
          onClose={() => {
            setShowApprovedModal(false);
            setApprovedSubmission(null);
            // Reset auto-open flag when modal is closed
            if (openSubmissionId) {
              setHasAutoOpened(true);
            }
          }}
          submission={approvedSubmission}
          onLeaveReview={handleLeaveReview}
        />
      )}

      {/* Revision Confirmation Modal - Rendered in portal to ensure it's on top */}
      {showRevisionConfirmModal && pendingRevisionData && createPortal(
        <div 
          className="dashboard-handin-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              cancelRevisionRequest();
            }
          }}
        >
          <div className="dashboard-handin-confirmation-modal">
            <div className="dashboard-handin-confirmation-header">
              <h3>Request Revision</h3>
              <button 
                className="dashboard-handin-confirmation-close"
                onClick={cancelRevisionRequest}
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="dashboard-handin-confirmation-content">
              <div className="dashboard-handin-warning-box">
                <AlertCircle size={24} className="dashboard-handin-warning-icon" />
                <div className="dashboard-handin-warning-text">
                  <h4>Revision Limit Warning</h4>
                  <p>You are about to submit revision request <strong>#{((pendingRevisionData?.revisionCount || pendingRevisionData?._originalData?.revisionCount || 0) + 1)} of 2</strong> for this submission.</p>
                </div>
              </div>
              
              <div className="dashboard-handin-submission-info">
                <h4>Submission Details:</h4>
                <p><strong>Creator:</strong> {pendingRevisionData?.creator?.name || pendingRevisionData?.creatorName || pendingRevisionData?._originalData?.creatorName || 'Unknown'}</p>
                <p><strong>Campaign:</strong> {pendingRevisionData?.campaign || pendingRevisionData?.campaignName || pendingRevisionData?._originalData?.campaignName || 'Unknown'}</p>
                <p><strong>Brief:</strong> {pendingRevisionData?.brief || pendingRevisionData?.briefName || pendingRevisionData?._originalData?.briefName || 'Unknown'}</p>
                <p><strong>Current Revisions:</strong> {(pendingRevisionData?.revisionCount || pendingRevisionData?._originalData?.revisionCount || 0)}/2</p>
                <p><strong>Revision Deadline:</strong> <span style={{color: '#059669', fontWeight: '600'}}>Creator has 5 business days to submit revision</span></p>
              </div>
            </div>
            
            <div className="dashboard-handin-confirmation-actions">
              <button 
                className="dashboard-handin-confirmation-cancel"
                onClick={cancelRevisionRequest}
              >
                Cancel
              </button>
              <button 
                className="dashboard-handin-confirmation-submit"
                onClick={confirmRevisionRequest}
              >
                Submit Revision Request ({((pendingRevisionData?.revisionCount || pendingRevisionData?._originalData?.revisionCount || 0) + 1)}/2)
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
};

export default DashboardHandIn;